import { PageBuilderTextFieldPropertiesModel, PageBuilderTextPropertiesModel } from "@de-trustleap/shared-models";
import { BaseWidget } from "./baseWidget";

export class TextFieldWidget extends BaseWidget<PageBuilderTextFieldPropertiesModel> {
    private disableInlineStyles: boolean;

    constructor(properties: PageBuilderTextFieldPropertiesModel, disableInlineStyles = false) {
        super(properties);
        this.disableInlineStyles = disableInlineStyles;
    }

    async render(): Promise<HTMLElement> {
        const isMultiLine = (this.properties.minLines ?? 1) > 1;
        const element = isMultiLine ? document.createElement('textarea') : document.createElement('input');

        if (!isMultiLine) {
            (element as HTMLInputElement).type = 'text';
        }

        element.id = this.generateId();

        if (!this.disableInlineStyles) {
            this.applyInputStyles(element, isMultiLine);
            this.handlePlaceholder(element);
        } else {
            // Nur Platzhalter-Text (ohne Inline-Placeholder-Styles)
            (element as HTMLInputElement | HTMLTextAreaElement).placeholder = this.properties.placeHolderTextProperties?.text ?? "";
        }

        if (this.properties.isRequired) {
            element.setAttribute('required', 'true');
        }

        return element;
    }

    private generateId(): string {
        return `textfield-${crypto.randomUUID().slice(0, 8)}`;
    }

    private applyInputStyles(element: HTMLElement, isMultiLine: boolean) {
        const textProps: PageBuilderTextPropertiesModel = this.properties.textProperties ?? new PageBuilderTextPropertiesModel({});

        this.applyStyles(element, {
            width: `${this.properties.width ?? 200}px`,
            padding: '10px',
            boxSizing: 'border-box',
            border: `1px solid ${this.formatColor(this.properties.borderColor, '#999999')}`,
            backgroundColor: this.formatColor(this.properties.backgroundColor, '#FFFFFF'),
            borderRadius: '5px',
            fontSize: `${textProps.fontSize ?? 16}px`,
            color: this.formatColor(textProps.color, '#000000'),
            fontFamily: textProps.fontFamily ?? 'Arial, sans-serif',
            textAlign: textProps.alignment ?? 'left',
            lineHeight: `${textProps.lineHeight ?? 1.5}`,
        });

        if (isMultiLine) {
            const minLines = this.properties.minLines ?? 3;
            const height = minLines * (textProps.fontSize ?? 16) * (textProps.lineHeight ?? 1.5);

            this.applyStyles(element, {
                height: `${height}px`,
                resize: 'none',
            });

            (element as HTMLTextAreaElement).rows = minLines;
        }
    }

    private handlePlaceholder(element: HTMLElement) {
        const placeholderProps = this.properties.placeHolderTextProperties;
        if (!placeholderProps) return;

        const text = placeholderProps.text ?? "";
        const requiredMark = this.properties.isRequired ? " *" : "";
        (element as HTMLInputElement | HTMLTextAreaElement).placeholder = text + requiredMark;

        this.setPlaceholderStyles(element.id, {
            color: this.formatColor(placeholderProps.color, '#999999'),
            fontSize: `${placeholderProps.fontSize ?? 16}px`,
            fontFamily: placeholderProps.fontFamily ?? 'Arial, sans-serif',
            textAlign: placeholderProps.alignment ?? 'left',
        });
    }

    private setPlaceholderStyles(elementId: string, styles: { color: string; fontSize: string; fontFamily: string; textAlign: string }) {
        let styleElement = document.getElementById('textfield-placeholder-style') as HTMLStyleElement;
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'textfield-placeholder-style';
            document.head.appendChild(styleElement);
        }

        styleElement.textContent += `
            #${elementId}::placeholder {
                color: ${styles.color} !important;
                font-size: ${styles.fontSize};
                font-family: ${styles.fontFamily};
                text-align: ${styles.textAlign};
            }
        `;
    }
}
